import { CloudGovErrorCodeLiterals } from "../domain/api-error";

type ErrorMessageMapping = {
  [key in CloudGovErrorCodeLiterals]?: string;
};

const ERROR_CODE_MESSAGES: ErrorMessageMapping = {
  bad_request: "入力内容に問題があります。",
  "bad_request.version_outdated":
    "データが更新されています。画面を更新して再度実行してください。",
  "bad_request.too_large_record_count": "レコード数が多すぎます。",
  "bad_request.invalid_user_email_token": "無効なメールトークンです。",
  "bad_request.inconsistent_pdf_schema_setting":
    "PDFスキーマの設定に問題があります。",
  "bad_request.procedure_not_match": "手続きの内容が一致しません。",
  "bad_request.invalid_csv_format": "CSVフォーマットが無効です。",
  "bad_request.same_as_current_password": "現在のパスワードと同じです。",
  unauthorized: "認証が必要です。再度ログインしてください。",
  forbidden: "この操作を実行する権限がありません。",
  not_found: "指定されたリソースが見つかりません。",
  "unauthorized.email_auth_required": "メール認証が必要です。",
  "unauthorized.tmp_password_expired":
    "仮パスワードの期限が切れています。再度申請してください。",
  "forbidden.not_permitted_network_access":
    "このネットワークからのアクセスは許可されていません。",
  "forbidden.user_login_locked":
    "アカウントがロックされています。管理者に連絡してください。",
  "forbidden.user_just_login_locked":
    "アカウントが一時的にロックされています。しばらく時間をおいて再度お試しください。",
  "bad_request.ttzk_portal.shortcut_max_count_error":
    "ショートカットの登録上限に達しました。",
  "bad_request.proc_management.tag_priority_duplication_error":
    "表示順が重複しています。表示順を変更して再度実行してください。",
  "not_found.procedure_before_publish": "公開前の手続きです。",
  "applications.tenant_process_result_unchangeable":
    "処理結果を変更できません。",
  "applications.pdf_field_render_failure":
    "PDFフィールドの描画に失敗しました。",
  "applications.pdf_has_optional_content":
    "PDFにオプショナルコンテンツが含まれています。",
  "bad_request.invalid_mail_template": "メールテンプレートが無効です。",
  sending_mail_error: "メールの送信に失敗しました。",
  "service_account.invalid_input.already_exists": "既に存在しています。",
  default: "エラーが発生しました。",
};

/**
 * HTTP status codeに基づいたエラーメッセージを取得します
 */
export function getMessageForStatusCode(status: number): string {
  switch (status) {
    case 400:
      return "入力内容に問題があります。";
    case 401:
      return "認証が必要です。再度ログインしてください。";
    case 403:
      return "この操作を実行する権限がありません。";
    case 404:
      return "指定されたリソースが見つかりません。";
    case 500:
      return "サーバーでエラーが発生しました。時間をおいて再度お試しください。";
    default:
      return "エラーが発生しました。時間をおいて再度お試しください。";
  }
}

/**
 * エラーコードに基づいた具体的なエラーメッセージを取得します
 */
export function getMessageForErrorCode(
  code: CloudGovErrorCodeLiterals,
): string {
  return (
    ERROR_CODE_MESSAGES[code] ||
    ERROR_CODE_MESSAGES.default ||
    "エラーが発生しました。"
  );
}

/**
 * エラーオブジェクトからユーザーフレンドリーなエラーメッセージを生成します
 */
export function getFriendlyErrorMessage(error: any): string {
  // APIエラーコードがある場合はそれを優先
  const errorCode = error?.response?.data?.code as CloudGovErrorCodeLiterals;
  if (errorCode) {
    return getMessageForErrorCode(errorCode);
  }

  // HTTPステータスコードがある場合はそれを使用
  const statusCode = error?.response?.status;
  if (statusCode) {
    return getMessageForStatusCode(statusCode);
  }

  // エラーメッセージがある場合はそれを使用
  const errorMessage = error?.response?.data?.msg || error?.message;
  if (errorMessage) {
    return `エラーが発生しました: ${errorMessage}`;
  }

  // それ以外の場合はデフォルトメッセージ
  return "エラーが発生しました。時間をおいて再度お試しください。";
}
